import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Search, Flower2, Sparkles, Heart, AlertTriangle } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "@/hooks/use-toast";
import { PageContainer } from "@/components/pro/PageContainer";
import { PageHeader } from "@/components/pro/PageHeader";
import logoIcon from "@/assets/regard-de-nature-logo.png";

interface BachFlower {
  id: string;
  name: string;
  description: string;
  main_effects: string[];
  symptoms_keywords: string[];
  contraindications: string[];
  usage_protocol: string;
}

const BachFlowersLibrary: React.FC = () => {
  const navigate = useNavigate();
  const [flowers, setFlowers] = useState<BachFlower[]>([]);
  const [filteredFlowers, setFilteredFlowers] = useState<BachFlower[]>([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [isLoading, setIsLoading] = useState(true);
  const [selectedCategory, setSelectedCategory] = useState("all");

  useEffect(() => {
    loadFlowers();
  }, []);

  useEffect(() => {
    filterFlowers();
  }, [searchTerm, flowers, selectedCategory]);

  const loadFlowers = async () => {
    try {
      const { data, error } = await supabase
        .from("bach_flowers")
        .select("*")
        .order("name");

      if (error) throw error;

      setFlowers(data || []);
    } catch (error) {
      console.error("Erreur chargement fleurs:", error);
      toast({
        title: "Erreur",
        description: "Impossible de charger les fleurs de Bach",
        variant: "destructive",
      });
    } finally {
      setIsLoading(false);
    }
  };

  const filterFlowers = () => {
    let filtered = flowers;

    if (searchTerm) {
      const searchLower = searchTerm.toLowerCase();
      filtered = filtered.filter(
        (flower) =>
          flower.name.toLowerCase().includes(searchLower) ||
          flower.description.toLowerCase().includes(searchLower) ||
          flower.symptoms_keywords.some((keyword) =>
            keyword.toLowerCase().includes(searchLower)
          ) ||
          flower.main_effects.some((effect) =>
            effect.toLowerCase().includes(searchLower)
          )
      );
    }

    setFilteredFlowers(filtered);
  };

  const getCategoryFromKeywords = (keywords: string[]): string => {
    const emotionKeywords = ["peur", "anxiété", "colère", "tristesse", "jalousie", "panique"];
    const confidenceKeywords = ["confiance", "estime", "affirmation", "infériorité"];
    const energyKeywords = ["fatigue", "épuisement", "surmenage", "motivation"];
    const mentalKeywords = ["rumination", "concentration", "indécision", "pensées"];

    const keywordsStr = keywords.join(" ").toLowerCase();

    if (emotionKeywords.some(k => keywordsStr.includes(k))) return "Émotions";
    if (confidenceKeywords.some(k => keywordsStr.includes(k))) return "Confiance";
    if (energyKeywords.some(k => keywordsStr.includes(k))) return "Énergie";
    if (mentalKeywords.some(k => keywordsStr.includes(k))) return "Mental";
    
    return "Autre";
  };

  return (
    <PageContainer>
      <PageHeader
        title="Fleurs de Bach"
        subtitle="Bibliothèque des 38 fleurs de Bach originales"
        icon={<Flower2 className="w-6 h-6 sm:w-7 sm:h-7" />}
        iconColor="accent"
      />

      <div className="space-y-8">

        {/* Recherche */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Search className="w-5 h-5" />
              Rechercher une fleur
            </CardTitle>
            <CardDescription>
              Recherchez par nom, symptômes ou effets thérapeutiques
            </CardDescription>
          </CardHeader>
          <CardContent>
            <Input
              placeholder="Ex: anxiété, peur, tristesse, confiance..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full"
            />
          </CardContent>
        </Card>

        {/* Statistiques */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-2 sm:gap-4">
          <Card>
            <CardContent className="pt-6">
              <div className="text-center">
                <div className="text-3xl font-bold text-primary">{flowers.length}</div>
                <div className="text-sm text-muted-foreground">Fleurs disponibles</div>
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="pt-6">
              <div className="text-center">
                <div className="text-3xl font-bold text-primary">{filteredFlowers.length}</div>
                <div className="text-sm text-muted-foreground">Résultats</div>
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="pt-6">
              <div className="text-center">
                <div className="text-3xl font-bold text-primary">38</div>
                <div className="text-sm text-muted-foreground">Élixirs originaux</div>
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="pt-6">
              <div className="text-center">
                <div className="text-3xl font-bold text-primary">100%</div>
                <div className="text-sm text-muted-foreground">Naturelles</div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Liste des fleurs */}
        {isLoading ? (
          <div className="text-center py-12">
            <p className="text-muted-foreground">Chargement des fleurs de Bach...</p>
          </div>
        ) : filteredFlowers.length === 0 ? (
          <Card>
            <CardContent className="py-12 text-center">
              <p className="text-muted-foreground">Aucune fleur trouvée pour cette recherche.</p>
            </CardContent>
          </Card>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6">
            {filteredFlowers.map((flower) => (
              <Card key={flower.id} className="hover:shadow-lg transition-shadow">
                <CardHeader>
                  <div className="flex items-start justify-between">
                    <CardTitle className="text-xl">{flower.name}</CardTitle>
                    <Badge variant="secondary" className="text-xs">
                      {getCategoryFromKeywords(flower.symptoms_keywords)}
                    </Badge>
                  </div>
                  <CardDescription className="text-sm mt-2">
                    {flower.description}
                  </CardDescription>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div>
                    <h4 className="font-semibold text-sm mb-2">Effets principaux :</h4>
                    <div className="flex flex-wrap gap-2">
                      {flower.main_effects.map((effect, idx) => (
                        <Badge key={idx} variant="outline" className="text-xs">
                          {effect}
                        </Badge>
                      ))}
                    </div>
                  </div>

                  <div>
                    <h4 className="font-semibold text-sm mb-2">Indications :</h4>
                    <div className="flex flex-wrap gap-2">
                      {flower.symptoms_keywords.slice(0, 3).map((keyword, idx) => (
                        <Badge key={idx} variant="secondary" className="text-xs">
                          {keyword}
                        </Badge>
                      ))}
                      {flower.symptoms_keywords.length > 3 && (
                        <Badge variant="secondary" className="text-xs">
                          +{flower.symptoms_keywords.length - 3}
                        </Badge>
                      )}
                    </div>
                  </div>

                  <div>
                    <h4 className="font-semibold text-sm mb-2">Protocole :</h4>
                    <p className="text-xs text-muted-foreground line-clamp-3">
                      {flower.usage_protocol}
                    </p>
                  </div>

                  {flower.contraindications && flower.contraindications.length > 0 && 
                   flower.contraindications[0] !== "Aucune connue" && (
                    <div>
                      <h4 className="font-semibold text-sm mb-2 text-destructive">
                        Contre-indications :
                      </h4>
                      <ul className="text-xs text-muted-foreground list-disc list-inside">
                        {flower.contraindications.map((ci, idx) => (
                          <li key={idx}>{ci}</li>
                        ))}
                      </ul>
                    </div>
                  )}
                </CardContent>
              </Card>
            ))}
          </div>
        )}

        {/* Footer informatif */}
        <Card className="mt-8">
          <CardHeader>
            <CardTitle>À propos des Fleurs de Bach</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4 text-sm text-muted-foreground">
            <p>
              Les Fleurs de Bach sont 38 élixirs floraux mis au point dans les années 1930 par le Dr Edward Bach,
              médecin britannique. Ces élixirs sont destinés à harmoniser les états émotionnels négatifs ou
              perturbateurs.
            </p>
            <p>
              <strong>Important :</strong> Les Fleurs de Bach ne remplacent pas un traitement médical. Elles
              s'inscrivent dans une approche complémentaire de bien-être émotionnel. Consultez un professionnel
              de santé qualifié pour tout problème de santé.
            </p>
            <p>
              <strong>Posologie standard :</strong> 4 gouttes, 4 fois par jour. En cas de crise : 2 à 4 gouttes
              toutes les 15 minutes pendant la première heure, puis espacer. Cure-type de 3 semaines avec pause
              et réévaluation.
            </p>
          </CardContent>
        </Card>
      </div>
    </PageContainer>
  );
};

export default BachFlowersLibrary;